import { integrationApi } from "@repo/api";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { isIspmHost } from "../../../config/integrationTypeConfig";
import { theme } from "../../../constants/theme";
import { UIProvider, useUI } from "../../../contexts/UIContext";
import { useIntegrationById } from "../../../hooks/useIntegrationById";
import { useIntegrationTypeFeatures } from "../../../hooks/useIntegrationTypeFeatures";
import { AddIntegrationFormProps } from "../../../types/form";
import {
  type Frequency,
  DEFAULT_FREQUENCY,
  parseCronToSchedule,
} from "../../../utils/schedule";
import { FormSection } from "../../FormSection";
import {
  CorrelationFieldsProvider,
  fieldsToOptions,
} from "../../UserAccountCorrelation/CorrelationFieldsContext";
import { CorrelationSection } from "../../UserAccountCorrelation/CorrelationSection";
import { AdditionalConfigurationSection } from "./AdditionalConfigurationSection";
import { ConnectionDetailsSection } from "./ConnectionDetailsSection";
import { IntegrationTypeSection } from "../../shared/IntegrationTypeSection";
import { isFormValid } from "./utils";

function AWSIntegrationFormContent({
  config,
  integrationId,
  onSubmit,
}: AddIntegrationFormProps) {
  const intl = useIntl();
  const { formData, setFieldValue } = useUI();
  const [cftDownloaded, setCftDownloaded] = useState(false);
  const [showCftAlert, setShowCftAlert] = useState(false);
  const [cftError, setCftError] = useState<string | null>(null);
  const regionOptions = config.regions || [];
  const [selectedRegions, setSelectedRegions] = useState<
    Array<{ label: string; value: string; isChecked: boolean }>
  >(regionOptions.filter((r) => r.isChecked));
  const [importTime, setImportTime] = useState<Date | null>(null);
  const [frequency, setFrequency] = useState<Frequency>(DEFAULT_FREQUENCY);
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  // Fetch integration data in edit mode
  const { data: integrationData } = useIntegrationById(integrationId);

  const isIspm = isIspmHost();
  const { data: userFields } = useQuery({
    queryKey: ["correlationRuleFields", "user"],
    queryFn: () => integrationApi.getCorrelationRuleFields("user"),
    enabled: isIspm,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const { data: accountFields } = useQuery({
    queryKey: ["correlationRuleFields", "account"],
    queryFn: () => integrationApi.getCorrelationRuleFields("account"),
    enabled: isIspm,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const correlationUserOptions = useMemo(
    () => fieldsToOptions(userFields?.fields ?? []),
    [userFields],
  );
  const correlationAccountOptions = useMemo(
    () => fieldsToOptions(accountFields?.fields ?? []),
    [accountFields],
  );

  // Fetch feature names from the integration type features API (cached from IntegrationTypeSection)
  const { data: featuresResponse } = useIntegrationTypeFeatures(
    config.provider ?? "AWS",
  );
  const featureNames = featuresResponse?.features
    ? featuresResponse.features.map((f) => f.featureName)
    : [];

  // Fetch credentials in edit mode
  const { data: credentials } = useQuery({
    queryKey: ["integrationCredentials", integrationId],
    queryFn: async () => {
      if (!integrationId) {
        throw new Error("Integration ID is required");
      }
      return await integrationApi.getIntegrationCredentials(integrationId);
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const handleCftDownload = async () => {
    setCftError(null);
    const accountId = formData.account_id || "";
    const allowedRegions = selectedRegions.map((r) => r.value);
    try {
      await integrationApi.downloadCftFile(
        accountId,
        featureNames,
        allowedRegions,
        integrationId,
      );
      setCftDownloaded(true);
      setShowCftAlert(true);
    } catch (error) {
      if (
        error instanceof Error &&
        error.message === "AWS_ACCOUNT_ID_REQUIRED"
      ) {
        setCftError("AWS_ACCOUNT_ID_REQUIRED");
      } else {
        setCftError("GENERIC_ERROR");
      }
    }
  };

  // Track if we've initialized the form to prevent re-initialization
  const hasInitializedRef = useRef(false);

  // Initialize form data with default values or edit mode data
  useEffect(() => {
    if (integrationId && integrationData && credentials) {
      // Only initialize once when data is available
      if (hasInitializedRef.current) return;
      // Edit mode: prefill with API data
      setFieldValue("name", integrationData.displayName || "");
      setFieldValue(
        "description",
        (integrationData.metadata?.description as string) || "",
      );
      setFieldValue(
        "account_id",
        (integrationData.metadata?.accountId as string) || "",
      );
      setFieldValue("role_arn", credentials.data.roleArn || "");
      setFieldValue("external_id", credentials.data.externalId || "");

      // Set CFT downloaded to true since credentials exist
      setCftDownloaded(true);

      // Prefill regions from features
      if (integrationData.features && integrationData.features.length > 0) {
        const feature = integrationData.features[0];
        // Regions are in featureConfig.regions, not metadata
        const regionsStr = (feature.featureConfig?.regions as string) || "";
        const regionsList =
          regionsStr === "All"
            ? regionOptions.map((r) => r.value)
            : regionsStr.split(",").map((r) => r.trim());

        // Map regions: check matching regions from the list
        const updatedRegions = regionOptions
          .map((region) => ({
            ...region,
            isChecked: regionsList.includes(region.value),
          }))
          .filter((r) => r.isChecked);
        setSelectedRegions(updatedRegions);
      }

      // Prefill frequency and import time from schedule
      if (integrationData.features && integrationData.features.length > 0) {
        const feature = integrationData.features[0];
        if (feature.schedules && feature.schedules.length > 0) {
          const schedule = feature.schedules[0];
          if (schedule.cronExpression) {
            const parsed = parseCronToSchedule(schedule.cronExpression);
            setFrequency(parsed.frequency);
            if (parsed.time) {
              setImportTime(parsed.time);
            }
          }
        }
      }
      hasInitializedRef.current = true;
    } else if (!integrationId && !hasInitializedRef.current) {
      // Add mode: use default values (only initialize once)
      config.fields.forEach((field) => {
        if (field.defaultValue) {
          setFieldValue(field.id, field.defaultValue);
        } else if (field.isReadOnly && field.value) {
          setFieldValue(field.id, field.value);
        }
      });
      hasInitializedRef.current = true;
    }
    // Only depend on the actual data values, not array references
  }, [
    integrationId,
    integrationData?.id,
    credentials?.data?.roleArn,
    setFieldValue,
  ]);

  // Notify parent of form data changes and validation state
  useEffect(() => {
    if (onSubmitRef.current) {
      const formIsValidResult = isFormValid(
        formData,
        cftDownloaded,
        selectedRegions,
        importTime,
        frequency,
      );
      onSubmitRef.current({
        ...formData,
        regions: selectedRegions.map((r) => r.value),
        frequency,
        importTime,
        isValid: formIsValidResult,
        cftDownloaded,
        featureNames,
      });
    }
  }, [formData, selectedRegions, importTime, frequency, cftDownloaded]);

  return (
    <div className="Integrations-addIntegrationForm flex flex-col h-full w-full p-4">
      <div className="flex-1 overflow-auto p-4">
        <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
          <IntegrationTypeSection integrationType={config.provider ?? "AWS"} />
          <ConnectionDetailsSection
            config={config}
            cftDownloaded={cftDownloaded}
            showCftAlert={showCftAlert}
            cftError={cftError}
            selectedRegions={selectedRegions}
            onCftDownload={handleCftDownload}
            onCftAlertClose={() => setShowCftAlert(false)}
            onCftErrorClear={() => setCftError(null)}
            onRegionsChange={setSelectedRegions}
            roleArn={credentials?.data.roleArn}
            externalId={credentials?.data.externalId}
            isEditMode={!!integrationId}
          />
          {isIspm && !!integrationId && (
            <FormSection
              icon="User"
              title={intl.formatMessage({
                id: "s/4S0e",
                defaultMessage: "User Account Correlation",
                description: "User Account Correlation section title",
              })}
              subtitle={intl.formatMessage({
                id: "Q7LnPy",
                defaultMessage:
                  "Define rules for correlating user identities with their cloud accounts. These rules help establish the relationship between enterprise users and their cloud identities. You can update them whenever you like.",
                description: "User Account Correlation section subtitle",
              })}
            >
              <CorrelationFieldsProvider
                userFieldOptions={correlationUserOptions}
                accountFieldOptions={correlationAccountOptions}
              >
                <CorrelationSection integrationId={integrationId} />
              </CorrelationFieldsProvider>
            </FormSection>
          )}
          <AdditionalConfigurationSection
            initialTime={importTime}
            initialFrequency={frequency}
            onScheduleChange={(freq, time) => {
              setFrequency(freq as Frequency);
              setImportTime(time);
            }}
            isRequired={true}
            isCritical={frequency === "24hours" && importTime === null}
          />
        </div>
      </div>
    </div>
  );
}

export default function AWSIntegrationForm({
  config,
  integrationId,
  onClose,
  onSubmit,
}: AddIntegrationFormProps) {
  // Initialize form data from config (excluding static fields: use_case and services)
  // In edit mode, data will be prefilled in the useEffect hook
  const initialData: Record<string, string> = {};
  if (!integrationId) {
    config.fields.forEach((field) => {
      if (field.id !== "use_case" && field.id !== "services") {
        if (field.defaultValue) {
          initialData[field.id] = field.defaultValue;
        } else if (field.isReadOnly && field.value) {
          initialData[field.id] = field.value;
        }
      }
    });
  }

  return (
    <UIProvider initialData={initialData}>
      <AWSIntegrationFormContent
        config={config}
        integrationId={integrationId}
        onClose={onClose}
        onSubmit={onSubmit}
      />
    </UIProvider>
  );
}
